import { useNavigate } from "react-router-dom";
import { PhotoIcon } from "@heroicons/react/24/outline";

interface Outfit {
  id: string;
  name?: string;
  imageUrl?: string;
  occasion?: string;
  createdAt?: string;
}

interface OutfitsListProps {
  outfits: Outfit[];
  loading: boolean;
}

export default function OutfitsList({ outfits, loading }: OutfitsListProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-light tracking-wider uppercase text-gray-900 dark:text-white mb-6">
          OUTFITS FEATURING THIS ITEM
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-light tracking-wider uppercase text-gray-900 dark:text-white mb-6">
        OUTFITS FEATURING THIS ITEM
      </h2>

      {outfits.length === 0 ? (
        <div className="text-center py-12">
          <PhotoIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-light">
            This item hasn't been styled yet.
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
            Use the chat assistant to create your first outfit!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {outfits.map((outfit) => (
            <div
              key={outfit.id}
              onClick={() => navigate(`/lookbook/${outfit.id}`)}
              className="group cursor-pointer"
            >
              <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden mb-3 relative">
                {outfit.imageUrl ? (
                  <img
                    src={outfit.imageUrl}
                    alt={outfit.name || "Outfit"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PhotoIcon className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white text-sm font-light tracking-wider uppercase">
                    VIEW LOOK
                  </span>
                </div>
              </div>
              
              <div className="space-y-1">
                <h3 className="font-light text-gray-900 dark:text-white text-sm truncate">
                  {outfit.name || "Untitled Outfit"}
                </h3>
                {outfit.occasion && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {outfit.occasion}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
